/** Shared enums, constants and DTO shapes used by API and web. */
export const TRIP_STATUSES = [
    'requested',
    'confirmed',
    'dispatched',
    'executing',
    'on_hold',
    'completed',
    'ready_to_bill',
    'billed',
    'closed',
    'declined',
    'cancelled',
    'failed',
];
export const STOP_STATUSES = [
    'pending',
    'arrived',
    'servicing',
    'serviced',
    'departed',
];
export const STOP_TYPES = ['pickup', 'drop', 'return_to_base'];
export const ASSIGNMENT_ROLES = [
    'driver',
    'helper_1',
    'helper_2',
    'additional_helper',
];
export const SEEDED_ROLES = [
    'Admin',
    'Dispatcher',
    'Finance',
    'Ops Manager',
    'Crew',
    'Sales Agent',
    'Client Main',
    'Client Sub',
    'Client Viewer',
];
export const CHARGE_TYPES = [
    'base_lane_rate',
    'additional_drop',
    'waiting_time',
    'tolls',
    'fuel_adjustment',
    'cancellation',
    'return_leg',
    'accessorial',
    'manual',
];
export const EXCEPTION_TYPES = [
    'consignee_closed',
    'consignee_refuses',
    'no_signature',
    'wrong_address',
    'access_denied',
    'cargo_not_ready',
    'short_count_pickup',
    'short_count_drop',
    'over_count',
    'damage',
    'wrong_stop_scan',
    'vehicle_breakdown',
    'crew_no_show',
    'client_cancels_after_dispatch',
];
export const ZONE_SEED = [
    { code: 'Z1', name: 'NCR North', coverage: 'Valenzuela, Caloocan, Malabon, Navotas, Quezon City' },
    { code: 'Z2', name: 'NCR Central', coverage: 'Manila, San Juan, Mandaluyong, Pasig, Marikina' },
    { code: 'Z3', name: 'NCR South', coverage: 'Makati, Pasay, Taguig, Pateros, Parañaque, Las Piñas, Muntinlupa' },
    { code: 'Z4', name: 'North Corridor', coverage: 'Bulacan — Meycauayan to Baliuag, San Jose del Monte' },
    { code: 'Z5', name: 'East Corridor', coverage: 'Rizal — Cainta, Taytay, Antipolo, San Mateo, Rodriguez' },
    { code: 'Z6', name: 'South Corridor', coverage: 'Cavite Bacoor–Trece Martires; Laguna San Pedro–Calamba incl. Cabuyao' },
    { code: 'Z7', name: 'Clark Corridor', coverage: 'Pampanga — Apalit, San Fernando, Mexico, Angeles, Mabalacat, Clark Freeport' },
];
export const VEHICLE_TYPE_SEED = [
    { code: '6W_FWD', name: '6-Wheeler Forward', payloadKg: 7000, tollClass: 2, kmPerLitre: '4.0' },
    { code: '10W_WV', name: '10-Wheeler Wing Van', payloadKg: 12000, tollClass: 3, kmPerLitre: '3.0' },
];
export const CURRENCY = 'PHP';
export const VAT_RATE = 0.12;
export const TIMEZONE = 'Asia/Manila';
/** Parse a datetime-local value (YYYY-MM-DDTHH:mm) as wall time in Asia/Manila. */
export function parseManilaLocalDateTime(value) {
    const trimmed = value.trim();
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(trimmed)) {
        throw new Error(`Invalid datetime-local value: ${value}`);
    }
    const normalized = trimmed.length === 16 ? `${trimmed}:00` : trimmed;
    return new Date(`${normalized}+08:00`);
}
/** Format a Date for HTML datetime-local inputs in Asia/Manila. */
export function formatManilaLocalDateTime(date) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(date);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
    return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}
/** Calendar date YYYY-MM-DD in Asia/Manila (not UTC). */
export function formatManilaDate(value) {
    const date = typeof value === 'string' ? new Date(value) : value;
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(date);
}
/** Inclusive calendar end date for period ranges.
 * Handles exclusive Manila midnights and UTC calendar EODs that spill into the next Manila day.
 * When `periodStart` is provided and the end falls in a later Manila month, returns the
 * last calendar day of the start's Manila month (e.g. Aug → Aug 31, never Sep 1).
 */
export function formatInclusiveManilaPeriodEnd(periodEnd, periodStart) {
    const end = typeof periodEnd === 'string' ? new Date(periodEnd) : periodEnd;
    if (periodStart != null) {
        const startYmd = formatManilaDate(periodStart);
        const endYmd = formatManilaDate(end);
        if (endYmd.slice(0, 7) !== startYmd.slice(0, 7)) {
            return lastManilaDayOfMonth(startYmd);
        }
    }
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    }).formatToParts(end);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
    const hour = Number(get('hour'));
    const minute = Number(get('minute'));
    const second = Number(get('second'));
    // Exclusive first-of-next (00:00:00 Manila) → previous calendar day
    if (hour === 0 && minute === 0 && second === 0) {
        return formatManilaDate(new Date(end.getTime() - 1));
    }
    return formatManilaDate(end);
}
function lastManilaDayOfMonth(startYmd) {
    const year = Number(startYmd.slice(0, 4));
    const month = Number(startYmd.slice(5, 7));
    const nextMonth = month === 12 ? 1 : month + 1;
    const nextYear = month === 12 ? year + 1 : year;
    const pad = (n) => String(n).padStart(2, '0');
    const firstOfNext = new Date(`${nextYear}-${pad(nextMonth)}-01T00:00:00+08:00`);
    return formatManilaDate(new Date(firstOfNext.getTime() - 1));
}
/** Start/end instants for the Asia/Manila calendar day containing `at`. */
export function manilaDayBounds(at) {
    const ymd = formatManilaDate(at);
    const dayStart = new Date(`${ymd}T00:00:00+08:00`);
    const dayEnd = new Date(`${ymd}T23:59:59.999+08:00`);
    return { dayStart, dayEnd };
}
/** Human-readable date/time in Asia/Manila. */
export function formatManilaDateTime(value) {
    const date = typeof value === 'string' ? new Date(value) : value;
    return date.toLocaleString('en-PH', {
        timeZone: TIMEZONE,
        dateStyle: 'medium',
        timeStyle: 'short',
    });
}
export { ACTIVE_STATUSES, ELIGIBLE_NEXT_STATUSES, IMMOVABLE_STATUSES, TERMINAL_RUN_TRIP_STATUSES, canInitiateReorder, findNextEligibleTrip, isActiveTripStatus, isEligibleNextTripStatus, isImmovableTripStatus, isLegalReorder, isTerminalRunTripStatus, validateReorderMoves, } from './runQueueEligibility.js';
export * from './cargoCheck.js';
